import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type FooterNavLink = { label: string; url: string };
export type FooterNavConfig = {
  row_1?: FooterNavLink[];
  row_2?: FooterNavLink[];
  mobile?: FooterNavLink[];
};

const WEBSITE_KEYS = [
  "nav_links_footer",
  "cta_banner_heading",
  "cta_banner_subheading",
  "cta_banner_button_text",
  "cta_banner_button_url",
] as const;

export function useWebsiteSettings() {
  return useQuery({
    queryKey: ["website-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("key, value")
        .in("key", WEBSITE_KEYS as unknown as string[]);
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const row of data ?? []) map[row.key] = row.value;
      return map;
    },
    staleTime: 60_000,
  });
}

export function useUpdateWebsiteSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { error } = await supabase
        .from("app_settings")
        .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: "key" });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["website-settings"] });
      qc.invalidateQueries({ queryKey: ["app_settings"] });
    },
  });
}

export function parseFooterNav(raw?: string): FooterNavConfig {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as FooterNavConfig;
  } catch {
    return {};
  }
}
